/**
 * TriageView Component
 * Display triage data collected during reception
 *
 * @version 2025.1.0
 */

import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Divider,
  Alert,
} from '@mui/material';
import {
  Favorite as HeartIcon,
  Thermostat as TempIcon,
  MonitorWeight as WeightIcon,
  Height as HeightIcon,
  Air as OxygenIcon,
  Speed as BpIcon,
  Notes as NotesIcon,
} from '@mui/icons-material';

import { TriageData, formatBloodPressure } from '../../../types/medicalRecord';

interface TriageViewProps {
  triageData: TriageData | null;
}

// BMI classification
function getBMIClassification(bmi: number | null): { label: string; color: 'success' | 'warning' | 'error' | 'info' } {
  if (bmi === null) return { label: '-', color: 'info' };
  if (bmi < 18.5) return { label: 'Abaixo do peso', color: 'warning' };
  if (bmi < 25) return { label: 'Normal', color: 'success' };
  if (bmi < 30) return { label: 'Sobrepeso', color: 'warning' };
  if (bmi < 35) return { label: 'Obesidade I', color: 'error' };
  if (bmi < 40) return { label: 'Obesidade II', color: 'error' };
  return { label: 'Obesidade III', color: 'error' };
}

// Blood pressure classification
function getBPClassification(systolic: number | null, diastolic: number | null): { label: string; color: 'success' | 'warning' | 'error' | 'info' } {
  if (systolic === null || diastolic === null) return { label: '-', color: 'info' };

  if (systolic < 120 && diastolic < 80) return { label: 'Normal', color: 'success' };
  if (systolic < 130 && diastolic < 85) return { label: 'Normal limítrofe', color: 'success' };
  if (systolic < 140 && diastolic < 90) return { label: 'Pré-hipertensão', color: 'warning' };
  if (systolic < 160 && diastolic < 100) return { label: 'HAS Estágio 1', color: 'warning' };
  if (systolic < 180 && diastolic < 110) return { label: 'HAS Estágio 2', color: 'error' };
  return { label: 'HAS Estágio 3', color: 'error' };
}

// Heart rate classification
function getHRClassification(hr: number | null): { label: string; color: 'success' | 'warning' | 'error' | 'info' } {
  if (hr === null) return { label: '-', color: 'info' };
  if (hr < 60) return { label: 'Bradicardia', color: 'warning' };
  if (hr <= 100) return { label: 'Normal', color: 'success' };
  return { label: 'Taquicardia', color: 'warning' };
}

// SpO2 classification
function getSpO2Classification(spo2: number | null): { label: string; color: 'success' | 'warning' | 'error' | 'info' } {
  if (spo2 === null) return { label: '-', color: 'info' };
  if (spo2 >= 95) return { label: 'Normal', color: 'success' };
  if (spo2 >= 90) return { label: 'Hipoxemia leve', color: 'warning' };
  return { label: 'Hipoxemia', color: 'error' };
}

// Temperature classification
function getTempClassification(temp: number | null): { label: string; color: 'success' | 'warning' | 'error' | 'info' } {
  if (temp === null) return { label: '-', color: 'info' };
  if (temp < 35) return { label: 'Hipotermia', color: 'warning' };
  if (temp <= 37.5) return { label: 'Normal', color: 'success' };
  if (temp <= 38.5) return { label: 'Febrícula', color: 'warning' };
  return { label: 'Febre', color: 'error' };
}

interface VitalSignCardProps {
  icon: React.ReactNode;
  title: string;
  value: string;
  classification: { label: string; color: 'success' | 'warning' | 'error' | 'info' };
  unit?: string;
}

const VitalSignCard: React.FC<VitalSignCardProps> = ({
  icon,
  title,
  value,
  classification,
  unit,
}) => (
  <Card variant="outlined">
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {icon}
        <Typography variant="subtitle2" color="text.secondary">
          {title}
        </Typography>
      </Box>
      <Typography variant="h5" gutterBottom>
        {value}
        {unit && (
          <Typography component="span" variant="body2" color="text.secondary">
            {' '}
            {unit}
          </Typography>
        )}
      </Typography>
      <Chip label={classification.label} color={classification.color} size="small" />
    </CardContent>
  </Card>
);

export const TriageView: React.FC<TriageViewProps> = ({ triageData }) => {
  if (!triageData) {
    return (
      <Box>
        <Alert severity="info">
          Triagem não realizada. Os dados de sinais vitais não foram coletados durante o check-in.
        </Alert>
      </Box>
    );
  }

  const bpClassification = getBPClassification(
    triageData.bloodPressureSystolic,
    triageData.bloodPressureDiastolic
  );
  const hrClassification = getHRClassification(triageData.heartRate);
  const tempClassification = getTempClassification(triageData.temperature);
  const spo2Classification = getSpO2Classification(triageData.oxygenSaturation);
  // Ensure bmi is a number (may come as string from API)
  const bmiValue = triageData.bmi !== null ? Number(triageData.bmi) : null;
  const bmiClassification = getBMIClassification(bmiValue);

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
        Dados da Triagem
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Coletados em:{' '}
        {triageData.createdAt
          ? new Date(triageData.createdAt).toLocaleString('pt-BR')
          : 'Data não disponível'}
      </Typography>
      <Grid container spacing={2}>
        {/* Pressão Arterial */}
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 4
          }}>
          <VitalSignCard
            icon={<BpIcon color="error" />}
            title="Pressão Arterial"
            value={formatBloodPressure(
              triageData.bloodPressureSystolic,
              triageData.bloodPressureDiastolic
            )}
            classification={bpClassification}
          />
        </Grid>

        {/* Frequência Cardíaca */}
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 4
          }}>
          <VitalSignCard
            icon={<HeartIcon color="error" />}
            title="Frequência Cardíaca"
            value={triageData.heartRate?.toString() || '-'}
            unit="bpm"
            classification={hrClassification}
          />
        </Grid>

        {/* Temperatura */}
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 4
          }}>
          <VitalSignCard
            icon={<TempIcon color="warning" />}
            title="Temperatura"
            value={triageData.temperature?.toString() || '-'}
            unit="°C"
            classification={tempClassification}
          />
        </Grid>

        {/* SpO2 */}
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 4
          }}>
          <VitalSignCard
            icon={<OxygenIcon color="info" />}
            title="Saturação O₂"
            value={triageData.oxygenSaturation?.toString() || '-'}
            unit="%"
            classification={spo2Classification}
          />
        </Grid>

        {/* Peso */}
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 4
          }}>
          <VitalSignCard
            icon={<WeightIcon color="primary" />}
            title="Peso"
            value={triageData.weight?.toString() || '-'}
            unit="kg"
            classification={{ label: '-', color: 'info' }}
          />
        </Grid>

        {/* Altura */}
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 4
          }}>
          <VitalSignCard
            icon={<HeightIcon color="primary" />}
            title="Altura"
            value={triageData.height?.toString() || '-'}
            unit="cm"
            classification={{ label: '-', color: 'info' }}
          />
        </Grid>
      </Grid>
      {/* IMC */}
      {bmiValue !== null && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <WeightIcon color="secondary" />
                    <Typography variant="subtitle2" color="text.secondary">
                      IMC (Índice de Massa Corporal)
                    </Typography>
                  </Box>
                  <Typography variant="h5" gutterBottom>
                    {bmiValue.toFixed(1)}
                    <Typography component="span" variant="body2" color="text.secondary">
                      {' '}
                      kg/m²
                    </Typography>
                  </Typography>
                  <Chip
                    label={bmiClassification.label}
                    color={bmiClassification.color}
                    size="small"
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}
      {/* Observações */}
      {triageData.observations && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ my: 2 }} />
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <NotesIcon color="action" />
                <Typography variant="subtitle2" color="text.secondary">
                  Observações da Triagem
                </Typography>
              </Box>
              <Typography variant="body2">{triageData.observations}</Typography>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default TriageView;
